import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';
import Image, { ImageProps } from '@/components/atoms/image/image';
import Button, { ButtonProps } from '@/components/molecules/button/button';
import {
  AboveFoldProvider,
  useAboveFoldContext,
} from '@/libraries/contexts/above-fold-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './image-slider.module.scss';

export interface SliderImageItem {
  /** The alt text of the image */
  altText: string;
  /** The url of the image */
  url: string;
}

export interface ImageSliderProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether to use a compact version */
  compact?: boolean;
  /** Content that should be rendered inside the first slide */
  firstSlideOverlay?: React.ReactNode;
  /** Indicates whether to ignore minimum size for images */
  ignoreMinimumSize?: boolean;
  /** Array of images to show in the slider */
  images: SliderImageItem[];
  /** Threshold that triggers the call for more images */
  interactionThreshold?: number;
  /** An event that passes the active index on slide change */
  onActiveIndexChange?: (index: number) => void;
  /** An event that is triggered to fetch more images */
  onFetchMoreImages?: () => void;
  /** An event that is triggered on touch end */
  onTouchEnd?: () => void;
  /** An event that is triggered on touch start */
  onTouchStart?: () => void;
  /** Indicates whether the first image should be loaded immediately */
  priority?: boolean;
  /** Trigger to reset the swiper position */
  resetPosition?: boolean;
  /** Indicates whether the navigation buttons should be shown */
  showNavigation?: boolean;
  /** Indicates whether the navigation buttons should be shown at the bottom */
  showNavigationOnBottom?: boolean;
}

const SWIPER_CONFIGURATION: SwiperOptions = {
  keyboard: true,
  lazyPreloadPrevNext: 1,
  slidesPerView: 1,
  spaceBetween: 0,
  speed: 500,
  wrapperClass: styles.swiperWrapper,
};

const IMAGE_SRC_SET: ImageProps['srcSet'] = {
  desktop: {
    width: 400,
  },
  laptop: {
    width: 400,
  },
  tablet: {
    width: 705,
  },
};

const ImageSlider = ({
  className,
  compact = false,
  firstSlideOverlay,
  images = [],
  interactionThreshold,
  onActiveIndexChange,
  onFetchMoreImages,
  onTouchEnd,
  onTouchStart,
  priority = false,
  resetPosition = false,
  showNavigation = true,
  showNavigationOnBottom = false,
  ignoreMinimumSize = false,
}: ImageSliderProps) => {
  const { t } = useI18n();
  const isAboveFold = useAboveFoldContext();
  const [displayImages, setDisplayImages] = useState(images);
  const [pendingImages, setPendingImages] = useState<SliderImageItem[] | null>(
    null,
  );
  const [interactionCount, setInteractionCount] = useState(0);
  const [isMoreFetched, setIsMoreFetched] = useState(false);
  const swiperRef = useRef<SwiperInstance | null>(null);

  useEffect(() => {
    if (swiperRef.current?.animating) {
      setPendingImages(images);
    } else {
      setDisplayImages(images);
      setPendingImages(null);
    }
  }, [images]);

  useEffect(() => {
    if (!swiperRef.current || !onFetchMoreImages || isMoreFetched) return;

    if (Math.abs(interactionCount) === interactionThreshold) {
      onFetchMoreImages();
      setIsMoreFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactionCount]);

  useEffect(() => {
    if (!swiperRef.current) return;

    if (!resetPosition) return;

    swiperRef.current.slideTo(0, 0);
  }, [resetPosition]);

  const handleInteraction = (increment: number) => {
    setInteractionCount(prevCount => {
      return prevCount + increment;
    });
  };

  const handleClickNext: ButtonProps['onClick'] = event => {
    event?.preventDefault();
    event?.stopPropagation();

    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handleClickPrevious: ButtonProps['onClick'] = event => {
    event?.preventDefault();
    event?.stopPropagation();

    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleSlideChangeTransitionEnd = slide => {
    if (pendingImages) {
      setDisplayImages(pendingImages);
      setPendingImages(null);
    }

    onActiveIndexChange?.(slide.realIndex);
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasBottomNavigation]: showNavigationOnBottom,
        [styles.hasCenteredNavigation]: !showNavigationOnBottom,
        [styles.isCompact]: compact,
        [styles.isIgnoringMinimumSize]: ignoreMinimumSize,
      })}
    >
      <Swiper
        {...SWIPER_CONFIGURATION}
        className={styles.swiper}
        loop={displayImages.length > 1}
        onSlideChangeTransitionEnd={slide =>
          handleSlideChangeTransitionEnd(slide)
        }
        onSlideNextTransitionEnd={() => handleInteraction(1)}
        onSlidePrevTransitionEnd={() => handleInteraction(-1)}
        onSwiper={swiper => (swiperRef.current = swiper)}
        onTouchEnd={() => onTouchEnd?.()}
        onTouchStart={() => onTouchStart?.()}
      >
        {displayImages.map((image, imageIndex) => (
          <SwiperSlide key={imageIndex} className={styles.swiperSlide}>
            {/* Only the first slide is visible on load, so the others never get priority */}
            <AboveFoldProvider value={isAboveFold && imageIndex === 0}>
              <Image
                alt={image.altText}
                className={styles.image}
                priority={priority && imageIndex === 0}
                src={image.url}
                srcSet={IMAGE_SRC_SET}
                unoptimized
                width={328}
              />
            </AboveFoldProvider>

            {imageIndex === 0 && firstSlideOverlay}
          </SwiperSlide>
        ))}
      </Swiper>

      {showNavigation && displayImages.length > 1 && (
        <React.Fragment>
          <Button
            aria-label={t('items.common.words.previousImage')}
            className={classNames(styles.button, styles.isPrevious)}
            icon="ChevronLeft"
            isSmall
            onClick={handleClickPrevious}
            type="button"
            variant="tertiary"
          />

          <Button
            aria-label={t('items.common.words.nextImage')}
            className={classNames(styles.button, styles.isNext)}
            icon="ChevronRight"
            isSmall
            onClick={handleClickNext}
            type="button"
            variant="tertiary"
          />
        </React.Fragment>
      )}
    </div>
  );
};

ImageSlider.displayName = 'ImageSlider';

export default ImageSlider;
